import { CurrenciesPage } from '@/admin/CurrenciesPage'
import { TaxRatesPage } from '@/admin/TaxRatesPage'
import { NumberingPage } from '@/admin/NumberingPage'
import { SettingsPage } from '@/admin/SettingsPage'
import { ThresholdsPage } from '@/admin/ThresholdsPage'
import { UnitsPage } from '@/admin/UnitsPage'
import { DealReasonsPage } from '@/admin/DealReasonsPage'
import { MailSettingsPage } from '@/mail/MailSettingsPage'
import { InboxQueuePage } from '@/mail/InboxQueuePage'
import { AiSettingsPage } from '@/admin/AiSettingsPage'
import { IntegrationsPage } from '@/admin/IntegrationsPage'
import { TelephonySettingsPage } from '@/telephony/TelephonySettingsPage'
import { MessengerSettingsPage } from '@/messenger/MessengerSettingsPage'
import { MessengerInboxPage } from '@/messenger/MessengerInboxPage'
import { AuditPage } from '@/admin/AuditPage'
import { CustomFieldsPage } from '@/admin/CustomFieldsPage'
import { TagsPage } from '@/admin/TagsPage'
import { FunnelsPage } from '@/admin/FunnelsPage'
import { LeadRulesPage } from '@/admin/LeadRulesPage'
import { AutomationPage } from '@/admin/AutomationPage'
import { UsersPage } from '@/admin/UsersPage'
import { RolesPage } from '@/admin/RolesPage'
import { ForecastModelPage } from '@/forecast/ForecastModelPage'
import { LeadScoreModelPage } from '@/leadscore/LeadScoreModelPage'
import { SupportInsightsPage } from '@/support/SupportInsightsPage'
import { ClientRiskPage } from '@/risk/ClientRiskPage'
import { SupportRoutingPage } from '@/support/SupportRoutingPage'
import { AssistantPage } from '@/ai/AssistantPage'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useApp } from './AppContext'
import { canOpenPath, NAVIGATION, startPath, TOP_ITEMS } from './navigation'
import { AppLayout } from '@/components/AppLayout'
import { AccessDenied } from '@/pages/AccessDenied'
import { NotFound } from '@/pages/NotFound'
import { Placeholder } from '@/pages/Placeholder'
import { EntityListPage } from '@/lists/EntityListPage'
import { EntityCardPage } from '@/card/EntityCardPage'
import { CounterpartyCreatePage } from '@/card/CounterpartyCreatePage'
import { ProductCreatePage } from '@/card/ProductForm'
import { ProductCategoriesPage } from '@/card/ProductCategoriesPage'
import { DealsPage } from '@/card/DealsPage'
import { DealCreatePage } from '@/card/DealCreatePage'
import { LeadCreatePage } from '@/card/LeadCreatePage'
import { LeaveCreatePage } from '@/card/LeaveCreatePage'
import { StocktakeCreatePage } from '@/card/StocktakeCreatePage'
import { ReceiptCreatePage, TransferCreatePage } from '@/card/WarehouseDocCreatePages'
import { DashboardsPage } from '@/analytics/DashboardsPage'
import { MyDayPage } from '@/myday/MyDayPage'
import { ReportBuilderPage } from '@/analytics/ReportBuilderPage'
import { SalesReportsPage } from '@/analytics/SalesReportsPage'
import { FinanceReportsPage } from '@/analytics/FinanceReportsPage'
import { WarehouseReportsPage } from '@/analytics/WarehouseReportsPage'
import { WarehousesPage } from '@/warehouse/WarehousesPage'
import { PriceListsPage } from '@/pricing/PriceListsPage'
import { PriceListPage } from '@/pricing/PriceListPage'
import { SalesPlansPage } from '@/plans/SalesPlansPage'
import { WarehouseTopologyPage } from '@/warehouse/WarehouseTopologyPage'
import { SchedulesPage } from '@/analytics/SchedulesPage'
import { ReconciliationPage } from '@/payments/ReconciliationPage'
import { CashBookPage } from '@/payments/CashBookPage'
import { FiscalPage } from '@/payments/FiscalPage'
import { ApprovalsPage } from '@/approvals/ApprovalsPage'
import { TasksPage } from '@/approvals/TasksPage'
import { CalendarPage } from '@/calendar/CalendarPage'
import { NotificationsPage } from '@/notifications/NotificationsPage'
import { AuthCallback, SignIn } from '@/auth/AuthScreens'
import type { NavItem } from './navigation'

/**
 * Роутинг с учётом прав (ТЗ §5.1).
 *
 * Проверка на маршруте, а не только в меню: прямая ссылка минует навигацию, и без неё
 * пользователь увидит экран раздела, которого у него нет.
 */

/**
 * Собственные экраны по коду из навигации (#76, #78, #79).
 *
 * Switch, а не таблица `Record<screen, Component>`: `noFallthroughCasesInSwitch` вместе с
 * типом-объединением даёт ошибку компиляции на неучтённом варианте, а таблица молча
 * вернула бы `undefined` и отрисовала пустой экран. Забытый экран должен ломать сборку, а
 * не выглядеть пустым разделом.
 *
 * Один switch на аналитику и оплату, а не два: признак «у пункта свой экран» один, и
 * второй разбор того же поля означал бы два места, где список экранов перечислен, — а
 * расходятся такие места молча.
 */
function OwnScreen({ screen }: { screen: NonNullable<NavItem['screen']> }) {
  switch (screen) {
    // «Мой день»: стартовый экран сотрудника — задачи, сделки без шага, лиды, счета.
    case 'my-day':
      return <MyDayPage />
    // Дашборды (#77): сетка виджетов поверх сохранённых отчётов, а не список строк.
    case 'dashboards':
      return <DashboardsPage />
    case 'report-builder':
      return <ReportBuilderPage />
    case 'sales-reports':
      return <SalesReportsPage />
    case 'finance-reports':
      return <FinanceReportsPage />
    case 'warehouse-reports':
      return <WarehouseReportsPage />
    case 'report-schedules':
      return <SchedulesPage />
    // Склады и топология: список складов; страница склада — свой маршрут `/warehouses/:id`.
    case 'warehouse-topology':
      return <WarehousesPage />
    // Категории товаров (V60): дерево, а не список строк — перенос ветки и запрет цикла.
    case 'product-categories':
      return <ProductCategoriesPage />
    // Прайс-листы: справочник; страница прайса с ценами — свой маршрут `/price-lists/:id`.
    case 'price-lists':
      return <PriceListsPage />
    // Планы продаж (#119): квартал, план-факт по менеджерам и отделам, прогноз по плану.
    case 'sales-plans':
      return <SalesPlansPage />
    // Качество модели прогноза сделок (#128): метрики на отложенной выборке и обучение.
    case 'forecast-model':
      return <ForecastModelPage />
    // Качество модели оценки лидов (#129): метрики, правило баллов рядом, обучение по исходам.
    case 'lead-score-model':
      return <LeadScoreModelPage />
    // Разбор обращений ИИ (#132): сводка за период и правила маршрутизации.
    case 'support-insights':
      return <SupportInsightsPage />
    // Клиенты в зоне риска (#135).
    case 'client-risk':
      return <ClientRiskPage />
    case 'admin-support-routing':
      return <SupportRoutingPage />
    // Ассистент по данным CRM (#136).
    case 'assistant':
      return <AssistantPage />
    // Подписки (#94): список каталога `billing.recurring` под правом счетов; карточка —
    // маршрут `/subscriptions/:id` ниже.
    case 'subscriptions':
      return <EntityListPage entity="billing.recurring" resource="billing.invoice" titleKey="nav.subscriptions" />
    // Оплата (#78): сверка это рабочий процесс, касса — журнал со сторнированием,
    // фискализация — список долгов перед ОФД. Ни одно из трёх не выражается
    // настраиваемым списком: список отвечает «какие строки», а эти экраны — «что
    // сделать с тем, что не легло само».
    case 'reconciliation':
      return <ReconciliationPage />
    case 'cash-book':
      return <CashBookPage />
    case 'fiscal':
      return <FiscalPage />
    // Сквозные экраны (#79): очередь на подпись, свои задачи, центр уведомлений.
    case 'approvals':
      return <ApprovalsPage />
    case 'tasks':
      return <TasksPage />
    case 'notifications':
      return <NotificationsPage />
    // Очередь разбора входящих писем (#121).
    case 'mail-inbox':
      return <InboxQueuePage />
    // Очередь разбора диалогов мессенджеров (#125).
    case 'messenger-inbox':
      return <MessengerInboxPage />
    // Календарь задач и встреч (#113): сетка времени с переносом, а не список строк.
    case 'calendar':
      return <CalendarPage />
    // Администрирование (#97): справочники правятся построчно с проверкой на сервере,
    // а не таблицей импорта — каждая правка меняет поведение системы для всех.
    case 'admin-numbering':
      return <NumberingPage />
    case 'admin-thresholds':
      return <ThresholdsPage />
    case 'admin-currencies':
      return <CurrenciesPage />
    case 'admin-tax-rates':
      return <TaxRatesPage />
    case 'admin-units':
      return <UnitsPage />
    case 'admin-deal-reasons':
      return <DealReasonsPage />
    case 'admin-mail':
      return <MailSettingsPage />
    case 'admin-ai':
      return <AiSettingsPage />
    case 'admin-telephony':
      return <TelephonySettingsPage />
    case 'admin-messengers':
      return <MessengerSettingsPage />
    case 'admin-integrations':
      return <IntegrationsPage />
    case 'admin-funnels':
      return <FunnelsPage />
    case 'admin-lead-rules':
      return <LeadRulesPage />
    case 'admin-automation':
      return <AutomationPage />
    case 'admin-audit':
      return <AuditPage />
    case 'admin-tags':
      return <TagsPage />
    case 'admin-custom-fields':
      return <CustomFieldsPage />
    case 'admin-users':
      return <UsersPage />
    case 'admin-roles':
      return <RolesPage />
    case 'admin-settings':
      return <SettingsPage />
  }
}

/** Обёртка, закрывающая маршрут без права. */
function Guarded({ children }: { children: React.ReactNode }) {
  const { user } = useApp()
  const location = useLocation()

  if (!canOpenPath(user, location.pathname)) {
    return <AccessDenied />
  }
  return <>{children}</>
}

export function Router() {
  const { user, isLoadingUser, userError, t } = useApp()
  const location = useLocation()

  // Возврат от Keycloak обрабатывается до проверки пользователя: в этот момент его
  // ещё нет, и любая другая ветка увела бы на повторный вход по кругу.
  if (location.pathname === '/callback') {
    return <AuthCallback />
  }

  if (isLoadingUser) return <p>{t('app.loading')}</p>
  if (userError || !user) return <SignIn />

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<Navigate to={firstAvailablePath(user)} replace />} />
        {TOP_ITEMS.map((item) => (
          <Route
            key={item.path}
            path={item.path}
            element={<Guarded>{item.screen ? <OwnScreen screen={item.screen} /> : null}</Guarded>}
          />
        ))}
        {NAVIGATION.flatMap((section) => section.items).flatMap((item) => [
          <Route
            key={item.path}
            path={item.path}
            element={
              <Guarded>
                {/* Настраиваемый список там, где сервер объявил сущность в реестре
                    (#48); остальные разделы пока заглушка. Показать пустую таблицу
                    вместо заглушки значило бы выдать ненаписанный экран за сломанный. */}
                {item.screen ? (
                  /* Собственный экран, а не список (#76, #78). Отчёт — это формула
                     (конверсия по истории стадий, ABC по накопленной доле), а не выборка
                     со сдвинутым фильтром; сверка платежей — рабочий процесс из трёх
                     шагов, а не таблица. Настраиваемым списком ни то ни другое не
                     выражается. */
                  <OwnScreen screen={item.screen} />
                ) : item.listEntity === 'sales.deal' ? (
                  /* У сделок две формы одного и того же — таблица и канбан (#71), и они
                     делят фильтр. Своя ветка, а не флаг в NavItem: вторая форма
                     представления пока одна, и обобщать её на одном примере значило бы
                     выдумать механизм раньше второго случая. */
                  <DealsPage
                    entity={item.listEntity}
                    resource={item.resource}
                    titleKey={item.labelKey}
                  />
                ) : item.listEntity ? (
                  <EntityListPage
                    entity={item.listEntity}
                    resource={item.resource}
                    titleKey={item.labelKey}
                  />
                ) : (
                  <Placeholder titleKey={item.labelKey} />
                )}
              </Guarded>
            }
          />,
          /* Карточка живёт под путём своего списка (`/counterparties/:id`), а не под
             отдельным `/cards/...`: путь списка уже проверяется `canOpenPath`, и своя
             ветка означала бы вторую проверку прав, которая однажды разойдётся с первой.
             Маршрут заводится только там, где есть сущность списка — карточке нечего
             читать без каталога колонок (#66). */
          /* Заведение контрагента (#70) стоит ПЕРЕД маршрутом карточки: иначе `/new`
             разберётся как идентификатор записи, и экран создания превратится в «запись
             не найдена». Своя ветка нужна потому, что у создания записи ещё нет, а
             карточке #66 без неё нечего читать. Пока такой экран один — заводить общий
             механизм «создание любой сущности» на одном примере значило бы выдумать
             обобщение раньше второго случая. */
          item.listEntity === 'sales.counterparty' ? (
            <Route
              key={`${item.path}/new`}
              path={`${item.path}/new`}
              element={
                <Guarded>
                  <CounterpartyCreatePage />
                </Guarded>
              }
            />
          ) : null,
          // Второй экран создания (#100) — сделка; общего механизма всё ещё нет: у двух
          // экранов общего меньше, чем различий (у сделки воронка, у контрагента дубли).
          item.listEntity === 'sales.lead' ? (
            <Route
              key={`${item.path}/new`}
              path={`${item.path}/new`}
              element={
                <Guarded>
                  <LeadCreatePage />
                </Guarded>
              }
            />
          ) : null,
          item.listEntity === 'sales.deal' ? (
            <Route
              key={`${item.path}/new`}
              path={`${item.path}/new`}
              element={
                <Guarded>
                  <DealCreatePage />
                </Guarded>
              }
            />
          ) : null,
          // Подача заявки на отсутствие (#94): тот же порядок — перед маршрутом карточки.
          item.listEntity === 'hr.leave' ? (
            <Route
              key={`${item.path}/new`}
              path={`${item.path}/new`}
              element={
                <Guarded>
                  <LeaveCreatePage />
                </Guarded>
              }
            />
          ) : null,
          // Оформление приёмки и перемещения из офиса (#94): тот же порядок — перед карточкой.
          item.listEntity === 'warehouse.receipt' ? (
            <Route
              key={`${item.path}/new`}
              path={`${item.path}/new`}
              element={
                <Guarded>
                  <ReceiptCreatePage />
                </Guarded>
              }
            />
          ) : null,
          item.listEntity === 'warehouse.transfer' ? (
            <Route
              key={`${item.path}/new`}
              path={`${item.path}/new`}
              element={
                <Guarded>
                  <TransferCreatePage />
                </Guarded>
              }
            />
          ) : null,
          // Открытие инвентаризации (#94), и по товару: тот же порядок — перед карточкой.
          item.listEntity === 'warehouse.stocktake' ? (
            <Route
              key={`${item.path}/new`}
              path={`${item.path}/new`}
              element={
                <Guarded>
                  <StocktakeCreatePage />
                </Guarded>
              }
            />
          ) : null,
          // Заведение товара (V60): тот же порядок — перед маршрутом карточки.
          item.listEntity === 'sales.product' ? (
            <Route
              key={`${item.path}/new`}
              path={`${item.path}/new`}
              element={
                <Guarded>
                  <ProductCreatePage />
                </Guarded>
              }
            />
          ) : null,
          // Страница склада (зоны и ячейки). Права проверяет сама страница: путь с
          // идентификатором вне навигации, и `canOpenPath` его пропустил бы.
          item.screen === 'price-lists' ? (
            <Route
              key={`${item.path}/:id`}
              path={`${item.path}/:id`}
              element={
                <Guarded>
                  <PriceListPage />
                </Guarded>
              }
            />
          ) : null,
          item.screen === 'subscriptions' ? (
            <Route
              key={`${item.path}/:id`}
              path={`${item.path}/:id`}
              element={
                <Guarded>
                  <EntityCardPage entity="billing.recurring" />
                </Guarded>
              }
            />
          ) : null,
          item.screen === 'warehouse-topology' ? (
            <Route
              key={`${item.path}/:id`}
              path={`${item.path}/:id`}
              element={
                <Guarded>
                  <WarehouseTopologyPage />
                </Guarded>
              }
            />
          ) : null,
          item.listEntity ? (
            <Route
              key={`${item.path}/:id`}
              path={`${item.path}/:id`}
              element={
                <Guarded>
                  <EntityCardPage entity={item.listEntity} />
                </Guarded>
              }
            />
          ) : null,
        ])}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  )
}

/** Куда вести с корня — правило живёт рядом с навигацией (`startPath`). */
function firstAvailablePath(user: Parameters<typeof canOpenPath>[0]): string {
  return startPath(user)
}
